// City District map — mid-game urban business district.
// Extends RouteMap from route1.ts (same pattern: local type extensions until
// constants.ts is updated to include encounter/exit/trainer fields).

import {
  type RouteMap,
  type EncounterZone,
  type MapExit,
  type TrainerPlacement,
} from "./route1";
import { TileType } from "@/engine/constants";

// ─── Layout constants ─────────────────────────────────────────────────────────

const WIDTH = 22;
const HEIGHT = 28;

// Main boulevard: 4-tile-wide Boardwalk strip, columns 9-12 (inclusive).
const BLVD_X_START = 9;
const BLVD_X_END = 12; // inclusive

// ─── Tile builder ─────────────────────────────────────────────────────────────

function buildTileMaps(): { tileMap: number[][]; collisionMap: boolean[][] } {
  const tileMap: number[][] = [];
  const collisionMap: boolean[][] = [];

  for (let y = 0; y < HEIGHT; y++) {
    const tileRow: number[] = [];
    const collRow: boolean[] = [];

    for (let x = 0; x < WIDTH; x++) {
      const onBorder = x === 0 || x === WIDTH - 1 || y === 0 || y === HEIGHT - 1;
      const onBoulevard = x >= BLVD_X_START && x <= BLVD_X_END;

      // --- Outer border walls (except exit tiles) ---
      if (onBorder) {
        // Exit gaps: bottom center (x=11,y=27), top center (x=11,y=0),
        // left mid (x=0,y=14).
        const isExit =
          (x === 11 && y === 27) ||
          (x === 11 && y === 0) ||
          (x === 0 && y === 14);
        if (isExit) {
          tileRow.push(TileType.Boardwalk);
          collRow.push(false);
        } else {
          tileRow.push(TileType.Wall);
          collRow.push(true);
        }
        continue;
      }

      // --- Main boulevard (columns 9-12) ---
      if (onBoulevard) {
        tileRow.push(TileType.Boardwalk);
        collRow.push(false);
        continue;
      }

      // --- Left side (x 1-8) ---
      if (x <= 8) {
        // Building blocks: Wall clusters at y 1-3, 13-15, 25-26.
        // Sidewalk (Floor) corridor at y 1-3 sides and between buildings.
        // Finance plaza encounter zone: y 4-11, x 1-6 → Grass.
        const isFinancePlaza = x >= 1 && x <= 6 && y >= 4 && y <= 11;
        const isLeftBuilding =
          (y >= 1 && y <= 3) ||
          (y >= 12 && y <= 15) ||
          (y >= 25 && y <= 26);

        if (isFinancePlaza) {
          tileRow.push(TileType.Grass);
          collRow.push(false);
        } else if (isLeftBuilding) {
          tileRow.push(TileType.Wall);
          collRow.push(true);
        } else {
          // Sidewalk / walkable connector strips
          tileRow.push(TileType.BuildingFloor);
          collRow.push(false);
        }
        continue;
      }

      // --- Right side (x 13-21) ---
      if (x >= 13) {
        // Tech plaza encounter zone: y 4-11, x 15-20 → Grass.
        const isTechPlaza = x >= 15 && x <= 20 && y >= 4 && y <= 11;
        const isRightBuilding =
          (y >= 1 && y <= 3) ||
          (y >= 12 && y <= 15) ||
          (y >= 25 && y <= 26);

        if (isTechPlaza) {
          tileRow.push(TileType.Grass);
          collRow.push(false);
        } else if (isRightBuilding) {
          tileRow.push(TileType.Wall);
          collRow.push(true);
        } else {
          tileRow.push(TileType.BuildingFloor);
          collRow.push(false);
        }
        continue;
      }

      // Fallback (shouldn't be reached, but keeps the grid complete)
      tileRow.push(TileType.BuildingFloor);
      collRow.push(false);
    }

    tileMap.push(tileRow);
    collisionMap.push(collRow);
  }

  // --- Creative district: Grass strip y 16-23, x 1-20 (over Floor base) ---
  for (let y = 16; y <= 23; y++) {
    for (let x = 1; x <= 20; x++) {
      // Keep boulevard tiles; replace everything else with Grass
      if (x < BLVD_X_START || x > BLVD_X_END) {
        tileMap[y][x] = TileType.Grass;
        collisionMap[y][x] = false;
      }
    }
  }

  return { tileMap, collisionMap };
}

const { tileMap, collisionMap } = buildTileMaps();

// ─── Encounter zones ──────────────────────────────────────────────────────────

const encounterZones: EncounterZone[] = [
  {
    id: "city_finance",
    bounds: { x: 1, y: 4, w: 6, h: 8 },
    speciesPool: [
      { speciesId: "5",  weight: 35, levelMin: 18, levelMax: 28 }, // Qubit
      { speciesId: "6",  weight: 20, levelMin: 22, levelMax: 30 }, // Stonkzilla
      { speciesId: "19", weight: 30, levelMin: 18, levelMax: 25 }, // Ledgeling
      { speciesId: "26", weight: 15, levelMin: 25, levelMax: 32 }, // Cryptoquant
    ],
  },
  {
    id: "city_tech",
    bounds: { x: 15, y: 4, w: 6, h: 8 },
    speciesPool: [
      { speciesId: "3",  weight: 35, levelMin: 18, levelMax: 28 }, // Byte
      { speciesId: "4",  weight: 20, levelMin: 22, levelMax: 30 }, // Debuggor
      { speciesId: "21", weight: 30, levelMin: 18, levelMax: 25 }, // Scriptlet
      { speciesId: "22", weight: 15, levelMin: 25, levelMax: 32 }, // Algomancer
    ],
  },
  {
    id: "city_creative",
    bounds: { x: 1, y: 16, w: 20, h: 8 },
    speciesPool: [
      { speciesId: "7",  weight: 30, levelMin: 20, levelMax: 28 }, // Vibecat
      { speciesId: "17", weight: 30, levelMin: 20, levelMax: 28 }, // Buzzlet
      { speciesId: "18", weight: 20, levelMin: 25, levelMax: 32 }, // Trendlion
      { speciesId: "27", weight: 20, levelMin: 26, levelMax: 33 }, // Growthhive
    ],
  },
];

// ─── Exits ────────────────────────────────────────────────────────────────────

const exits: MapExit[] = [
  { x: 11, y: 27, targetMapId: "town_center", targetX: 10, targetY: 0  }, // south → town
  { x: 11, y: 0,  targetMapId: "corp_tower",  targetX: 11, targetY: 27 }, // north → corp tower
  { x: 0,  y: 14, targetMapId: "route_1",     targetX: 23, targetY: 14 }, // west  → route 1
];

// ─── Trainer placements ───────────────────────────────────────────────────────

const trainers: TrainerPlacement[] = [
  { trainerId: "trainer_city_a", x: 6,  y: 10 },
  { trainerId: "trainer_city_b", x: 16, y: 10 },
  { trainerId: "trainer_city_c", x: 11, y: 20 },
];

// ─── Map export ───────────────────────────────────────────────────────────────

export const CITY_DISTRICT_MAP: RouteMap = {
  id: "city_district",
  name: "City District",
  type: "overworld",
  width: WIDTH,
  height: HEIGHT,
  tileMap,
  collisionMap,
  interactables: [],
  encounterZones,
  exits,
  trainers,
};
